import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface GEDCOMFile {
  filename: string;
  size: number;
  size_mb: number;
}

const GEDCOMImportPage: React.FC = () => {
  const [files, setFiles] = useState<GEDCOMFile[]>([]);
  const [selectedFile, setSelectedFile] = useState<string>('');
  const [homePersonName, setHomePersonName] = useState<string>('Sebastian Shaw');
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const [importResult, setImportResult] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    loadGEDCOMFiles();
  }, []);

  const loadGEDCOMFiles = async () => {
    // Render's free tier can take ~60s to wake, so allow that before giving up
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 90000);
    try {
      const response = await fetch(`${API_URL}/import/gedcom/files`, { signal: controller.signal });
      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }
      const data = await response.json();
      setFiles(data.files || []);
    } catch (error) {
      console.error('Error loading GEDCOM files:', error);
      const reason = error instanceof Error && error.name === 'AbortError'
        ? 'no response after 90 seconds'
        : error instanceof Error ? error.message : 'unknown error';
      setImportResult(`❌ Could not reach the API at ${API_URL} (${reason})`);
    } finally {
      clearTimeout(timeout);
      setIsLoading(false);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      setImportResult('Please select a GEDCOM file to import');
      return;
    }

    setIsImporting(true);
    setImportResult('');

    try {
      const response = await fetch(`${API_URL}/import/gedcom`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          filename: selectedFile,
          home_person_name: homePersonName
        }),
      });

      const data = await response.json();
      
      if (response.ok) {
        setImportResult(`✅ ${data.message}`);
        // Refresh the file list
        loadGEDCOMFiles();
      } else {
        setImportResult(`❌ Error: ${data.detail || 'Import failed'}`);
      }
    } catch (error) {
      console.error('Error importing GEDCOM file:', error);
      setImportResult(`❌ Error: ${error instanceof Error ? error.message : 'Import failed'}`);
    } finally {
      setIsImporting(false);
    }
  };

  const handleClearDatabase = async () => {
    if (!window.confirm('Are you sure you want to clear all data from the database? This action cannot be undone!')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/import/gedcom/clear`, {
        method: 'POST',
      });

      const data = await response.json();
      
      if (response.ok) {
        setImportResult(`✅ ${data.message}`);
      } else {
        setImportResult(`❌ Error: ${data.detail || 'Clear failed'}`);
      }
    } catch (error) {
      console.error('Error clearing database:', error);
      setImportResult(`❌ Error: ${error instanceof Error ? error.message : 'Clear failed'}`);
    }
  };

  if (isLoading) {
    return (
      <div className="gedcom-import-page">
        <h1>GEDCOM Import</h1>
        <p>Loading GEDCOM files...</p>
      </div>
    );
  }

  return (
    <div className="gedcom-import-page">
      <h1>GEDCOM Import</h1>
      <p>Import your family tree data from GEDCOM files into the genealogy database.</p>

      <div className="import-section">
        <h2>Available GEDCOM Files</h2>
        {files.length === 0 ? (
          <p>No GEDCOM files found. Please place .ged files in the backend/gedcom/ directory.</p>
        ) : (
          <div className="file-list">
            {files.map((file) => (
              <div key={file.filename} className="file-item">
                <label>
                  <input
                    type="radio"
                    name="gedcomFile"
                    value={file.filename}
                    checked={selectedFile === file.filename}
                    onChange={(e) => setSelectedFile(e.target.value)}
                  />
                  <span className="file-name">{file.filename}</span>
                  <span className="file-size">({file.size_mb} MB)</span>
                </label>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="import-section">
        <h2>Import Settings</h2>
        <div className="form-group">
          <label htmlFor="homePersonName">Home Person Name:</label>
          <input
            type="text"
            id="homePersonName"
            value={homePersonName}
            onChange={(e) => setHomePersonName(e.target.value)}
            placeholder="Name of the home person (e.g., Sebastian Shaw)"
          />
          <small>This person will be used as the reference point for the family tree. The system will search for this name in the GEDCOM file.</small>
        </div>
      </div>

      <div className="import-section">
        <h2>Actions</h2>
        <div className="button-group">
          <button
            onClick={handleImport}
            disabled={!selectedFile || isImporting}
            className="import-button"
          >
            {isImporting ? 'Importing...' : 'Import GEDCOM File'}
          </button>
          
          <button
            onClick={handleClearDatabase}
            className="clear-button"
            style={{ marginLeft: '10px' }}
          >
            Clear Database
          </button>
        </div>
      </div>

      {importResult && (
        <div className={`import-result ${importResult.includes('✅') ? 'success' : 'error'}`}>
          {importResult}
        </div>
      )}

      <div className="import-section">
        <h2>Instructions</h2>
        <ol>
          <li>Place your GEDCOM (.ged) files in the backend/gedcom/ directory</li>
          <li>Refresh this page to see available files</li>
          <li>Select a file to import</li>
          <li>Set the Home Person Name (the main person in your family tree)</li>
          <li>Click "Import GEDCOM File" to import the data</li>
          <li>Use "Clear Database" to remove all data before importing fresh data</li>
        </ol>
      </div>
    </div>
  );
};

export default GEDCOMImportPage;
